import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { dt } from 'Root/Shared/utils/dynamicRS'

import { getLocalizedData, getLocalizedUrl } from '../../utils/localeURL'
import {
  loadTurnstile,
  TurnstileApi,
  TurnstileSize,
  TurnstileTheme,
  TurnstileWidgetId,
} from '../../utils/turnstile'
import { useTurnstileSettings } from '../../utils/turnstileContext'
import FormFieldCheckboxWidget from '../PuxForm/FormFieldInputCheckboxWidget'
// import FormFieldFileWidget from '../PuxForm/FormFieldInputFileWidget'
import FormFieldInputWidget from '../PuxForm/FormFieldInputWidget'
import FormFieldMultipleWidget from '../PuxForm/FormFieldMultipleWidget'
import FormFieldRadioWidget from '../PuxForm/FormFieldRadioWidget'
import FormFieldSubmitWidget from '../PuxForm/FormFieldSubmitWidget'
import FormFieldTextareaWidget from '../PuxForm/FormFieldTextareaWidget'
import FormFieldTextWidget from '../PuxForm/FormFieldTextWidget'
import { ContentLinkType } from '../PuxNavigation/PuxNavigation'
import { getFormData, sendFormData } from './puxFormHelper'
import * as WidgetBuilder from './WidgetBuilder'
import {
  WidgetAnimationPart,
  WidgetBase,
  WidgetMetadata,
  WidgetProperitesPart,
} from './WidgetBuilder'
import { WidgetContainerProps } from './WidgetContainer'

export interface FormElementValidion {
  formElementValidationRequired: boolean
  formElementValidationPattern?: string | RegExp
  formElementValidationMin?: number
  formElementValidationMax?: number
  formElementValidationMinLength?: number
  formElementValidationMaxLength?: number
}

export interface FormElementOptions {
  formElementOptionsDisabled: boolean
  formElementOptionsAutofocus: boolean
  formElementOptionsAutocomplete: boolean
  formElementOptionsMultiple: boolean
  formElementOptionsStep: number
  formElementOptionsCssClass: string
  formElementOptionsReadOnly: boolean
}

export interface FormElement {
  formElementDefaultValue: string
  formElementID: string
  formElementLabel: string
  formElementName: string
  formElementPlaceholder: string
}

export interface FormOptions {
  formAction: string
  formMethod: string
  formEncType: string
  formAntiForgeryToken: boolean
  formID: string
  formSuccessMessage: HtmlBody
  formCustomCssClass: string
  formShowLabels: boolean
  formRedirectUrl: ContentLinkType
}

export interface FormRecaptchaOptions {
  formValidateRecaptcha: boolean
}

const getTurnstileTheme = (value?: string): TurnstileTheme => {
  return value === `light` || value === `dark` || value === `auto`
    ? value
    : `auto`
}

const getTurnstileSize = (value?: string): TurnstileSize => {
  return value === `compact` ? value : `normal`
}

export interface Metadata {
  alignment: string
  size: number
}

export interface FormFieldTextContext {
  html: string
}

export interface HtmlBody {
  html: string
}

// types

interface BaseFieldInput {
  displayText: string
  contentType: string
}

export interface FormFieldInputType extends BaseFieldInput {
  puxFormElement: FormElement
  puxFormElementOptions: FormElementOptions
  puxFormElementValidation: FormElementValidion
  metadata: Metadata
  inputType: string
}

export interface FormFieldTextareaType extends BaseFieldInput {
  displayText: string
  contentType: string
  metadata: Metadata
  puxFormElement: FormElement
  puxFormElementOptions: FormElementOptions
  puxFormElementValidation: FormElementValidion
  textareaResize: boolean
  textareaRows: number
}

export interface FormFieldSubmitType extends BaseFieldInput {
  displayText: string
  formElementName: string
  buttonTheme: string
  buttonSize: string
  buttonType: string
  metadata: Metadata
}

export interface FormFieldText extends BaseFieldInput {
  displayText: string
  formFieldTextContext: FormFieldTextContext
  metadata: Metadata
  puxFormElementOptions: FormElementOptions
}

export interface FormFieldMultipleType extends BaseFieldInput {
  displayText: string
  metadata: Metadata
  multipleOptions: string
  multipleType: string
  puxFormElement: FormElement
  puxFormElementOptions: FormElementOptions
  puxFormElementValidation: FormElementValidion
}

export type FormFieldType =
  | FormFieldInputType
  | FormFieldMultipleType
  | FormFieldSubmitType
  | FormFieldText
  | FormFieldTextareaType

// widget
export interface PuxFormType extends WidgetBase {
  contentType: 'PuxForm' | 'PuxFormContainer'
  displayText: string
  puxFormRecaptchaOptions: FormRecaptchaOptions
  puxFormOptions: FormOptions
  metadata: Metadata
  puxWidgetProperties: WidgetBuilder.WidgetProperitesPart
  puxWidgetAnimation: WidgetBuilder.WidgetAnimationPart
  flow: {
    widgets: FormFieldType[]
  }
  mautic?: {
    formContainerMauticID: string | null
    formContainerSource: string | null
    formContainerSubmissionID: string | null
  }
}

export interface PuxFormContainerType {
  puxWidgetAnimation: WidgetAnimationPart
  puxWidgetProperties: WidgetProperitesPart
  metadata: WidgetMetadata | Metadata
  contentType: 'PuxFormContainer'
  contentItemId: string
  formContainerMauticID: string
  formContainerSource: string
  formContainerSubmissionID: string
  formContainerSelector: {
    contentItemIds: string[]
  }

}

export interface FormBuilderProps extends WidgetContainerProps {
  formBuilderData?: PuxFormType
}

const FormBuilder: FunctionComponent<FormBuilderProps> = (
  props: FormBuilderProps
) => {
  const turnstileSettings = useTurnstileSettings()
  const formBuilderData = props?.formBuilderData?.flow?.widgets ?? []
  const mauticData = props.formBuilderData?.mautic

  const [loading, setLoading] = useState<boolean>(false)
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [beErrors, setBeErrors] = useState<string[]>(undefined as never)
  const turnstileContainerRef = useRef<HTMLDivElement>(null)
  const turnstileApiRef = useRef<TurnstileApi>()
  const turnstileWidgetIdRef = useRef<TurnstileWidgetId>()
  const captchaPreloadAttemptedRef = useRef<boolean>(false)
  const captchaExecutionStartedRef = useRef<boolean>(false)
  const submissionInFlightRef = useRef<boolean>(false)
  const mountedRef = useRef<boolean>(true)
  const pendingCaptchaRef = useRef<{
    resolve: (token: string) => void
    reject: (error: Error) => void
  }>()
  const isTrialForm =
    props?.formBuilderData?.displayText === `mauticform_trialrequest`
  const useCaptcha =
    props?.formBuilderData?.puxFormRecaptchaOptions?.formValidateRecaptcha ??
    false
  const turnstileSiteKey = turnstileSettings?.turnstileSiteKey
  const turnstileTheme = getTurnstileTheme(turnstileSettings?.turnstileTheme)
  const turnstileSize = getTurnstileSize(turnstileSettings?.turnstileSize)
  // TODO: add email validation popup
  // const [showEmailValidationPopup, setShowEmailValidationPopup] = useState<boolean>(false)
  // const hasEmailField = formBuilderData.some(field => {
  //   if (field.contentType === 'PuxFormFieldInput') {
  //     const inputField = field as FormFieldInputType
  //     return inputField.inputType === 'email'
  //   }
  //   return false
  // })
  // const [stopFormSending, setStopFormSending] = useState<boolean>(hasEmailField)

  const myCustomOptions = formBuilderData.reduce((defaults, field) => {
    switch (field.contentType) {
      case `PuxFormFieldInput`:
      case `PuxFormFieldTextarea`:
        const fieldData = field as FormFieldInputType
        defaults[fieldData.puxFormElement.formElementName] =
          fieldData.puxFormElement.formElementDefaultValue
        return defaults
      case `PuxFormFieldMultiple`:
        const fieldDataMultiple = field as FormFieldMultipleType
        defaults[fieldDataMultiple.puxFormElement.formElementName] =
          fieldDataMultiple.puxFormElement.formElementDefaultValue
        return defaults
      default:
        return defaults
    }
  }, {})

  useEffect(() => {
    console.log(beErrors)
  }, [beErrors])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      const api = turnstileApiRef.current
      const widgetId = turnstileWidgetIdRef.current
      const pendingCaptcha = pendingCaptchaRef.current
      turnstileApiRef.current = undefined
      turnstileWidgetIdRef.current = undefined
      captchaExecutionStartedRef.current = false
      pendingCaptchaRef.current = undefined
      if (widgetId && api) {
        try {
          api.remove(widgetId)
        } catch {
          // The widget may already have removed itself.
        }
      }
      pendingCaptcha?.reject(new Error(`Turnstile widget was removed`))
    }
  }, [])

  const methods = useForm({ defaultValues: myCustomOptions })

  const generateMauticFieldData = (value: string, id: string, label: string): FormFieldInputType => {
    return {
      puxFormElement: {
        formElementDefaultValue: value,
        formElementID: id,
        formElementLabel: label,
        formElementName: id,
        formElementPlaceholder: ''
      },
      puxFormElementOptions: {
        formElementOptionsDisabled: false,
        formElementOptionsAutofocus: false,
        formElementOptionsAutocomplete: false,
        formElementOptionsMultiple: false,
        formElementOptionsStep: 0,
        formElementOptionsCssClass: '',
        formElementOptionsReadOnly: false
      },
      puxFormElementValidation: {
        formElementValidationRequired: true,
        formElementValidationPattern: undefined,
        formElementValidationMin: undefined,
        formElementValidationMax: undefined,
        formElementValidationMinLength: undefined,
        formElementValidationMaxLength: undefined
      },
      metadata: {
        alignment: '',
        size: 100
      },
      inputType: 'hidden',
      displayText: label,
      contentType: 'PuxFormFieldInput'
    }
  }

  const buildMautic = (mauticFields: typeof mauticData, idx: number): JSX.Element | undefined | null => {
    if (!mauticFields?.formContainerMauticID || !mauticFields?.formContainerSource || !mauticFields?.formContainerSubmissionID) {
      return null
    }

    const mauticIdField = generateMauticFieldData(mauticFields.formContainerMauticID, 'mauticform[formId]', 'Mautic Id')
    const sourceField = generateMauticFieldData(mauticFields.formContainerSource, 'mauticform[source]', 'Source')
    const submissionIdField = generateMauticFieldData(mauticFields.formContainerSubmissionID, 'mauticform[submission_form_identifier]', 'Submission Id')
    const messengerField = generateMauticFieldData('1', 'mauticform[messenger]', 'Messenger')

    return (
      <>
        {buildForm(mauticIdField, idx + 1)}
        {buildForm(sourceField, idx + 2)}
        {buildForm(submissionIdField, idx + 3)}
        {buildForm(messengerField, idx + 4)}
      </>
    )
  }

  const buildForm = (
    formField: FormFieldType,
    id: number
  ): JSX.Element | undefined => {
    switch (formField.contentType) {
      case `PuxFormFieldInput`:
        const inputProps = formField as FormFieldInputType

        switch (inputProps?.inputType) {
          case `checkbox`: {
            return (
              <FormFieldCheckboxWidget
                key={id}
                props={inputProps}
                widgetProperties={props?.formBuilderData?.puxWidgetProperties}
              />
            )
          }
          case `file`: {
            return undefined
            // return (
            //   <Suspense key={id} fallback={<></>}>
            //     <FormFieldFileWidget
            //       props={inputProps}
            //       widgetProperties={props?.formBuilderData?.puxWidgetProperties}
            //     />
            //   </Suspense>
            // )
          }
          default:
            return (
              <FormFieldInputWidget
                key={id}
                props={inputProps}
                widgetProperties={props?.formBuilderData?.puxWidgetProperties}
              />
            )
        }

      case `PuxFormFieldTextarea`:
        return (
          <FormFieldTextareaWidget
            key={id}
            props={formField as FormFieldTextareaType}
            widgetProperties={props?.formBuilderData?.puxWidgetProperties}
          />
        )

      case `PuxFormFieldSubmit`:
        return (
          <FormFieldSubmitWidget
            key={id}
            props={formField as FormFieldSubmitType}
            widgetProperties={props?.formBuilderData?.puxWidgetProperties}
          />
        )
      case `PuxFormFieldText`:
        return (
          <FormFieldTextWidget
            key={id}
            props={formField as FormFieldText}
            widgetProperties={props?.formBuilderData?.puxWidgetProperties}
          />
        )
      case `PuxFormFieldMultiple`:
        const multipleProps = formField as FormFieldMultipleType
        return multipleProps.multipleType === `select` ? (
          <FormFieldMultipleWidget
            key={id}
            props={multipleProps}
            widgetProperties={props?.formBuilderData?.puxWidgetProperties}
          />
        ) : (
          <FormFieldRadioWidget
            key={id}
            props={multipleProps}
            widgetProperties={props?.formBuilderData?.puxWidgetProperties}
          />
        )
      default:
        return <div key={id}>{JSON.stringify(formField, null, `\n`)}</div>
    }
  }

  const getFormActionUrl = (action: string) => {
    if (action.startsWith("$") && action.endsWith("$")) {
      return process.env.GATSBY_CMS_URL! + dt(action.slice(1, -1))
    }

    return process.env.GATSBY_CMS_URL! + action
  }

  const getCaptchaWhitelistUrl = () => {
    const projectPrefix = process.env.PROJECT?.toLowerCase().replace("gatsby", "") ?? "easyredmine"

    return process.env.GATSBY_CMS_URL! + "/" + projectPrefix + "/mautic/captcha-whitelist"
  }

  const prepareCaptcha = () => {
    if (
      useCaptcha &&
      !captchaPreloadAttemptedRef.current &&
      !window.location.search.includes(`easy_testing_bot`)
    ) {
      captchaPreloadAttemptedRef.current = true
      void loadTurnstile().catch(() => undefined)
    }
  }

  const rejectPendingCaptcha = (message: string) => {
    pendingCaptchaRef.current?.reject(new Error(message))
    pendingCaptchaRef.current = undefined
  }

  const renderCaptchaWidget = (
    api: TurnstileApi,
    container: HTMLDivElement
  ): TurnstileWidgetId => {
    if (!turnstileSiteKey) {
      throw new Error(`Turnstile site key is not configured`)
    }

    if (turnstileWidgetIdRef.current) {
      return turnstileWidgetIdRef.current
    }

    const widgetId = api.render(container, {
      sitekey: turnstileSiteKey,
      theme: turnstileTheme,
      size: turnstileSize,
      execution: `execute`,
      appearance: `interaction-only`,
      callback: (token) => {
        pendingCaptchaRef.current?.resolve(token)
        pendingCaptchaRef.current = undefined
      },
      'error-callback': (errorCode) => {
        console.error(`Cloudflare Turnstile challenge failed: ${errorCode}`)
        rejectPendingCaptcha(`Turnstile failed: ${errorCode}`)
        return true
      },
      'expired-callback': () => rejectPendingCaptcha(`Turnstile token expired`),
      'timeout-callback': () => rejectPendingCaptcha(`Turnstile timed out`),
      'unsupported-callback': () =>
        rejectPendingCaptcha(`Turnstile is not supported`),
      'response-field': false,
      retry: `never`,
    })
    turnstileApiRef.current = api
    turnstileWidgetIdRef.current = widgetId
    return widgetId
  }

  const resetCaptcha = () => {
    const widgetId = turnstileWidgetIdRef.current
    if (widgetId && turnstileApiRef.current) {
      try {
        turnstileApiRef.current.reset(widgetId)
      } catch {
        // The widget may have been removed while the request was pending.
      }
    }
    captchaExecutionStartedRef.current = false
  }

  const removeCaptcha = () => {
    const widgetId = turnstileWidgetIdRef.current
    if (widgetId && turnstileApiRef.current) {
      try {
        turnstileApiRef.current.remove(widgetId)
      } catch {
        // The widget may already have removed itself.
      }
    }
    turnstileApiRef.current = undefined
    turnstileWidgetIdRef.current = undefined
    captchaExecutionStartedRef.current = false
  }

  const getCaptchaToken = async (): Promise<string> => {
    if (!turnstileSiteKey) {
      throw new Error(`Turnstile site key is not configured`)
    }

    const container = turnstileContainerRef.current
    if (!container) {
      throw new Error(`Turnstile container is not available`)
    }
    const api = await loadTurnstile()
    if (!mountedRef.current || turnstileContainerRef.current !== container) {
      throw new Error(`Turnstile widget was removed`)
    }

    return new Promise<string>((resolve, reject) => {
      const pendingCaptcha = { resolve, reject }
      pendingCaptchaRef.current = pendingCaptcha
      try {
        const widgetId = renderCaptchaWidget(api, container)
        if (pendingCaptchaRef.current !== pendingCaptcha) {
          removeCaptcha()
          return
        }
        if (captchaExecutionStartedRef.current) {
          api.reset(widgetId)
        }
        captchaExecutionStartedRef.current = true
        api.execute(container)
      } catch (error) {
        if (pendingCaptchaRef.current === pendingCaptcha) {
          pendingCaptchaRef.current = undefined
        }
        reject(error instanceof Error ? error : new Error(`Turnstile failed`))
      }
    })
  }

  function IDGenerator() {
    const length = 77
    const timestamp = +new Date()

    const _getRandomInt = function (min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min
    }

    const ts = timestamp.toString()
    const parts = ts.split(``).reverse()
    let id = ``

    for (let i = 0; i < length; ++i) {
      const index = _getRandomInt(0, parts.length - 1)
      id += parts[index]
    }
    return id
  }

  async function handleFormSubmit(fieldValues: FieldValues) {
    if (submissionInFlightRef.current) {
      return
    }
    submissionInFlightRef.current = true
    setLoading(true)

    let validateCaptcha: boolean = useCaptcha
    const formActionUrl = getFormActionUrl(props?.formBuilderData?.puxFormOptions.formAction ?? "")
    const identifier = IDGenerator()
    // TODO: add email validation popup
    // if (stopFormSending) {
    //   setShowEmailValidationPopup(true)
    //   return
    // }

    if (window.location.search.includes("easy_testing_bot")) {
      try {
        validateCaptcha = await fetch(getCaptchaWhitelistUrl())
          .then(response => response.text())
          .then(text => {
            const onWhitelist = text === "true"
            if (useCaptcha && onWhitelist) {
              return false
            }
            return useCaptcha
          })
      } catch {
        setBeErrors([`Captcha validation failed. Please try again.`])
        submissionInFlightRef.current = false
        setLoading(false)
        return
      }
    }

    let token: string | null = null
    if (validateCaptcha) {
      try {
        token = await getCaptchaToken()
      } catch (error) {
        if (!mountedRef.current) return
        console.error(`Cloudflare Turnstile token generation failed`, error)
        resetCaptcha()
        setBeErrors([`Captcha validation failed. Please try again.`])
        submissionInFlightRef.current = false
        setLoading(false)
        return
      }
    }

    const data = getFormData(fieldValues, identifier)
    const queryParams = new URLSearchParams(window.location.search)
    const aBox = queryParams.get("a_box")
    if (aBox) {
      data.append("affiliate", aBox ?? "")
    }
    data.append(
      `FormSystemId`,
      props?.formBuilderData?.puxFormOptions?.formID ?? ``
    )
    data.append(
      `FormCulture`,
      getLocalizedData(process.env.LOCALE ?? `en`, process.env.PROJECT!).isoCode
    )
    if (validateCaptcha && token) {
      data.append(`cf-turnstile-response`, token)
    }
    try {
      const responseJson = await sendFormData(
        formActionUrl,
        data,
        ``
      )

      if (responseJson) {
        if (responseJson.status == `ok`) {
          removeCaptcha()
          if (isTrialForm) {
            window.dataLayer.push({
              'event': 'gaVirtualPageview',
              'gaVirtualPageviewUrl': '/virtual/Trial/01-Trial-form-submitted',
              'gaVirtualPageviewTitle': 'Virtual Trial/01-Trial-form-submitted',
              'eventCallback': function (containerId) {
                if (containerId.includes("GTM-")) {
                  window.dataLayer.push({
                    'event': 'gaEvent',
                    'gaEventData': {
                      'eventCategory': 'Trial',
                      'eventAction': '01-Trial-form-submitted',
                      'eventLabel': '-',
                      'nonInteraction': false
                    },
                    'eventCallback': function () {
                      window.dataLayer.push({ 'gaEventData': undefined });
                      // redirect to lobby
                      setTimeout(function () { window.location.replace("https://lobby.easy8.com/trial/" + identifier + "?brand=Easy8&lang=" + document.documentElement.lang.split("-")[0]); }, 1000);
                    }
                  });
                }
              }
            });
            // redirect to lobby
            setTimeout(() =>
              window.location.replace(
                `https://lobby.easy8.com/trial/` +
                identifier +
                `?brand=Easy8&lang=` +
                document.documentElement.lang.split("-")[0]
              ),
              4000
            )
            return
          } else {
            setFormSubmitted(true)
            const redirectUrl =
              props?.formBuilderData?.puxFormOptions?.formRedirectUrl
            if (redirectUrl?.url[0]) {
              if (redirectUrl?.internal[0]) {
                window.location.href =
                  getLocalizedUrl(redirectUrl.url[0])?.replace("//", "/")
              } else {
                window.location.href = redirectUrl.url
              }
              setLoading(false)
              return
            }
            setLoading(false)
            return
          }
        }
        setBeErrors(responseJson.errors)
      } else {
        setBeErrors([`Error submitting form. Please contact us.`])
      }

      if (validateCaptcha && turnstileWidgetIdRef.current) {
        resetCaptcha()
      }
      submissionInFlightRef.current = false
      setLoading(false)
    } catch {
      if (validateCaptcha) {
        resetCaptcha()
      }
      setBeErrors([`Error submitting form. Please contact us.`])
      submissionInFlightRef.current = false
      setLoading(false)
    }
  }

  return !formSubmitted ? (
    <>
      <FormProvider {...methods}>
        <form
          id={props.formBuilderData?.puxFormOptions.formID}
          className={loading ? `loading` : undefined}
          onSubmit={methods.handleSubmit(async (data) =>
            handleFormSubmit(data)
          )}
          onKeyDownCapture={prepareCaptcha}
          onPointerDownCapture={prepareCaptcha}
        >
          {mauticData ? buildMautic(mauticData, formBuilderData.length) : null}
          {formBuilderData.map((formField: FormFieldType, idx) =>
            buildForm(formField, idx)
          )}
          {useCaptcha ? <div ref={turnstileContainerRef} /> : null}
          {/* 
          TODO: add email validation popup
          {hasEmailField && showEmailValidationPopup && (
            <div className='alert alert-danger mb-20'>
              Please enter a valid email address.
              <button
                type='submit'
                className='btn btn-primary'
                onClick={() => setStopFormSending(false)}
              >
                Submit
              </button>
            </div>
          )} */}
        </form>
      </FormProvider>
      {beErrors?.length > 0 ? (
        <div className='alert alert-danger mb-20'>
          {beErrors?.map((error, i) => {
            return (
              <div key={i}>
                {error?.split(`: `)?.length > 1
                  ? error?.split(`: `)?.[1]
                  : (error as string)}
              </div>
            )
          })}
        </div>
      ) : undefined}
    </>
  ) : (
    <div>
      <div className='pux-form-success-text alert alert-success'>
        {props?.formBuilderData?.puxFormOptions?.formSuccessMessage}
      </div>
    </div>
  )
}

export default FormBuilder
